import { apiClient } from './client';
import type {
  Subscription,
  SubscriptionListItem,
  SubscriptionLookups,
  SubscriptionParamKind,
  SubscriptionParamOption,
  SubscriptionWrite,
} from '@/types/oscar';

/**
 * Typed wrappers over `/api/onsitelive-admin/subscriptions*` (Migration Plan
 * section 8) - OnBudget's "Subscription" screen. Reads are admin + helpdesk;
 * writes are admin only and refused (409) until the subscriptions cutover.
 */

const base = '/api/onsitelive-admin/subscriptions';

export function getSubscriptionLookups(signal?: AbortSignal): Promise<SubscriptionLookups> {
  return apiClient.get<SubscriptionLookups>(`${base}/lookups`, signal);
}

/** Up to 500, by description. Search matches description, report name, recipients, parameter 1 or the id. */
export function listSubscriptions(
  params: { search?: string; groupId?: number | null; includeInactive?: boolean },
  signal?: AbortSignal,
): Promise<SubscriptionListItem[]> {
  const qs = new URLSearchParams();
  if (params.search?.trim()) qs.set('search', params.search.trim());
  if (params.groupId) qs.set('groupId', String(params.groupId));
  if (params.includeInactive) qs.set('includeInactive', 'true');
  const q = qs.toString();
  return apiClient.get<SubscriptionListItem[]>(`${base}${q ? `?${q}` : ''}`, signal);
}

export function getSubscription(id: number, signal?: AbortSignal): Promise<Subscription> {
  return apiClient.get<Subscription>(`${base}/${id}`, signal);
}

export function createSubscription(body: SubscriptionWrite): Promise<Subscription> {
  return apiClient.post<Subscription>(base, body);
}

export function updateSubscription(id: number, body: SubscriptionWrite): Promise<Subscription> {
  return apiClient.patch<Subscription>(`${base}/${id}`, body);
}

/** The OnBudget parameter pickers. 503 = that list is not available yet (type the value). */
export function getSubscriptionParamOptions(
  kind: SubscriptionParamKind,
  params: { customer?: string; search?: string } = {},
  signal?: AbortSignal,
): Promise<SubscriptionParamOption[]> {
  const qs = new URLSearchParams({ kind });
  if (params.customer?.trim()) qs.set('customer', params.customer.trim());
  if (params.search?.trim()) qs.set('search', params.search.trim());
  return apiClient.get<SubscriptionParamOption[]>(`${base}/param-options?${qs.toString()}`, signal);
}
